import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Clock, User, GraduationCap } from 'lucide-react'
import { bookingService } from '@/api/services/bookingService'
import { Card } from '@/shared/ui/Card'
import { Spinner } from '@/shared/ui/Spinner'
import { StatusBadge } from '@/shared/ui/Badge'
import { cn } from '@/shared/utils'

const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// Local YYYY-MM-DD key (avoids UTC shifting a session onto the wrong day).
function dayKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type SessionEntry = {
  id: any
  bookingId: any
  start: Date
  end: Date | null
  status: string
  student: string
  trainer: string
  pkg: string
}

// Which bookings API feeds the calendar:
//  - 'schoolAdmin' → admin/booking/school/get-bookings-list  (school admin dashboard)
//  - 'admin'       → admin/booking/get-bookings-list          (super admin dashboard)
type UpcomingSchedulesSource = 'admin' | 'schoolAdmin'

export function UpcomingSchedules({ source = 'schoolAdmin' }: { source?: UpcomingSchedulesSource }) {
  const { t } = useTranslation()
  const WEEKDAYS = (t('dash.weekdays', { returnObjects: true }) as string[]) ?? WEEKDAYS_EN
  const MONTHS = (t('dash.months', { returnObjects: true }) as string[]) ?? MONTHS_EN
  const [view, setView] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() } })

  const { data, isLoading } = useQuery({
    queryKey: ['upcoming-schedules', source],
    queryFn: () => source === 'admin'
      ? bookingService.list({ page: 1, limit: 100 })
      : bookingService.listSchoolAdmin({ page: 1, limit: 100 }),
    staleTime: 60_000,
  })

  // Flatten every booking's sessions and bucket them by local calendar day.
  const byDay = useMemo(() => {
    const rows: any[] = (data as any)?.data?.bookings ?? (data as any)?.bookings ?? (data as any)?.data ?? []
    const map = new Map<string, SessionEntry[]>()
    for (const b of rows) {
      const student = b.user?.name ?? '—'
      const trainer = b.driver?.name ?? b.driver_details?.name ?? '—'
      const pkg = b.package?.package_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name
        ?? b.package?.package_translations?.[0]?.name ?? ''
      for (const s of (b.sessions ?? [])) {
        if (!s.start_time) continue
        const start = new Date(s.start_time)
        if (Number.isNaN(start.getTime())) continue
        const key = dayKey(start)
        const entry: SessionEntry = {
          id: s.id ?? `${b.id}-${key}`,
          bookingId: b.id,
          start,
          end: s.end_time ? new Date(s.end_time) : null,
          status: s.session_status ?? 'PENDING',
          student, trainer, pkg,
        }
        const list = map.get(key)
        if (list) list.push(entry); else map.set(key, [entry])
      }
    }
    for (const list of map.values()) list.sort((a, b) => a.start.getTime() - b.start.getTime())
    return map
  }, [data])

  // Build the calendar grid (leading blanks + days of the month).
  const cells = useMemo(() => {
    const firstDow = new Date(view.year, view.month, 1).getDay()
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate()
    const arr: (number | null)[] = Array.from({ length: firstDow }, () => null)
    for (let d = 1; d <= daysInMonth; d++) arr.push(d)
    while (arr.length % 7 !== 0) arr.push(null)
    return arr
  }, [view])

  const todayKey = dayKey(new Date())

  const goPrev = () => setView(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 })
  const goNext = () => setView(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 })

  return (
    <Card padding={false} className="h-full flex flex-col">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="font-bold text-gray-900">{t('dash.upcomingSchedules')}</h3>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner/></div>
      ) : (
        <div className="p-4 flex-1">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={goPrev} className="w-8 h-8 rounded-lg flex items-center justify-center text-brand-500 hover:bg-brand-50 transition-colors" aria-label={t('dash.prevMonth')}>
              <ChevronLeft size={18} className="rtl:rotate-180"/>
            </button>
            <div className="text-base font-bold text-gray-900">{MONTHS[view.month]} {view.year}</div>
            <button onClick={goNext} className="w-8 h-8 rounded-lg flex items-center justify-center text-brand-500 hover:bg-brand-50 transition-colors" aria-label={t('dash.nextMonth')}>
              <ChevronRight size={18} className="rtl:rotate-180"/>
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 bg-gray-50 rounded-lg mb-1">
            {WEEKDAYS.map(w => (
              <div key={w} className="text-center text-[11px] font-semibold text-gray-400 py-2">{w}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (day === null) return <div key={i} className="h-11"/>
              const key = dayKey(new Date(view.year, view.month, day))
              const sessions = byDay.get(key)
              const count = sessions?.length ?? 0
              const isToday = key === todayKey
              // Anchor the hover popover on the right half of the row toward the left, so it doesn't run off-card.
              const col = i % 7
              const popRight = col >= 4
              return (
                <div key={i} className={cn('group relative h-11 flex items-center justify-center rounded-lg text-sm transition-colors',
                  count > 0 ? 'font-semibold text-gray-900 hover:bg-brand-50' : 'text-gray-400',
                  isToday && 'ring-1 ring-brand-300 bg-brand-50/50')}
                >
                  {day}
                  {count > 0 && (
                    <span className="absolute top-1 right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {count}
                    </span>
                  )}

                  {/* Hover popover: the day's session list */}
                  {count > 0 && (
                    <div className={cn(
                      'invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity',
                      'absolute z-50 top-full w-60 rounded-xl border border-gray-200 bg-white shadow-xl p-2.5',
                      popRight ? 'right-0' : 'left-0',
                    )}>
                      <div className="text-[11px] font-bold text-gray-900 mb-1.5">
                        {new Date(view.year, view.month, day).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                        <span className="text-gray-400 font-medium"> · {count} {count !== 1 ? t('dash.sessions') : t('dash.session')}</span>
                      </div>
                      <div className="space-y-1.5 max-h-56 overflow-y-auto">
                        {sessions!.map((s) => (
                          <div key={s.id} className="rounded-lg border border-gray-100 bg-gray-50/60 px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-800">
                                <Clock size={10} className="text-gray-400"/>
                                {s.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                {s.end && <span className="text-gray-400 font-normal">–{s.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                              </span>
                              <StatusBadge status={s.status}/>
                            </div>
                            <div className="mt-1 flex flex-col gap-0.5 text-[10.5px] text-gray-500">
                              <span className="flex items-center gap-1 truncate"><GraduationCap size={10} className="text-gray-400 flex-shrink-0"/>{s.student}</span>
                              <span className="flex items-center gap-1 truncate"><User size={10} className="text-gray-400 flex-shrink-0"/>{s.trainer}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Card>
  )
}
